import { useState, useEffect } from 'react';
import { Box, TextField, Button, Typography, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../supabaseClient';
import { API_BASE } from '../api';
import { useDestrierTheme } from '../ThemeContext';
import { useAuth } from '../AuthContext';
import { trackLogin, trackWaitlistSignup, trackErrorDisplayed } from '../analytics';

export default function LoginPage() {
  const { c: _c } = useDestrierTheme();
  const c = { ..._c, accent: '#8A74E4' };
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState('login'); // 'login' | 'signup' | 'username'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistStatus, setWaitlistStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'already'
  const [waitlistPosition, setWaitlistPosition] = useState(null);

  const [signedOut, setSignedOut] = useState(false);
  const [logoClicks, setLogoClicks] = useState(0);
  const [showSecretAccess, setShowSecretAccess] = useState(false);
  const [secretMode, setSecretMode] = useState('access'); // 'access' | 'signup'
  const [chessUsername, setChessUsername] = useState('');
  const [firstName, setFirstName] = useState('');

  // Sign out any existing session when first landing on login page
  useEffect(() => {
    supabase.auth.signOut().then(() => setSignedOut(true));
  }, []);

  // After a successful login, redirect to dashboard
  useEffect(() => {
    if (signedOut && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, signedOut, navigate]);

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: c.bg }}>
        <CircularProgress sx={{ color: c.accent }} />
      </Box>
    );
  }



  // Login
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail || !password) {
      setError('Please fill in all fields.');
      setLoading(false);
      return;
    }

    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: trimmedEmail,
      password,
    });
    if (signInError) {
      setError(signInError.message);
      trackErrorDisplayed('login', signInError.message);
    } else {
      trackLogin('email');
    }
    setLoading(false);
  };

  const toggleMode = () => {
    setMode(mode === 'login' ? 'signup' : 'login');
    setError('');
    setSuccess('');
    setLogoClicks(0);
    setShowSecretAccess(false);
  };

  const handleLogoClick = () => {
    if (mode !== 'signup') return;
    const next = logoClicks + 1;
    setLogoClicks(next);
    if (next >= 5) setShowSecretAccess(true);
  };

  const handleSecretSignup = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedName = firstName.trim();
    const trimmedChess = chessUsername.trim().toLowerCase();
    if (!trimmedEmail || !password || !trimmedName || !trimmedChess) { setError('Please fill in all fields.'); return; }
    setError(''); setLoading(true);
    try {
      const res = await fetch(`https://api.chess.com/pub/player/${trimmedChess}`);
      if (!res.ok) { setError('Chess.com username not found.'); setLoading(false); return; }
    } catch { setError('Could not verify chess.com username.'); setLoading(false); return; }
    const { error: signUpError } = await supabase.auth.signUp({
      email: trimmedEmail, password,
      options: { data: { display_name: trimmedName, chess_com_username: trimmedChess } },
    });
    if (signUpError) { setError(signUpError.message); setLoading(false); return; }
    setSuccess('Account created! Check your email to confirm, then sign in.');
    setLoading(false);
  };

  const handleSecretAccess = async () => {
    const trimmed = chessUsername.trim().toLowerCase();
    if (!trimmed) { setError('Please enter a username.'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/check-username?username=${encodeURIComponent(trimmed)}`);
      const data = await res.json();
      if (!data.exists) { setError('This username is not in our database yet.'); setLoading(false); return; }
    } catch { setError('Could not verify username.'); setLoading(false); return; }
    setLoading(false);
    window.location.href = `/dashboard/weaknesses?username=${encodeURIComponent(trimmed)}`;
  };

  // ── Shared styles ──
  const textFieldSx = {
    mb: 2,
    '& .MuiOutlinedInput-root': {
      color: c.textDark,
      backgroundColor: c.bg,
      borderRadius: '100px',
      '& fieldset': { borderColor: `${c.textMuted}30` },
      '&:hover fieldset': { borderColor: c.accent },
      '&.Mui-focused fieldset': { borderColor: c.accent },
    },
    '& .MuiInputLabel-root': { color: c.textMuted },
    '& .MuiInputLabel-root.Mui-focused': { color: c.accent },
    '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
      WebkitBoxShadow: `0 0 0 100px ${c.bg} inset`,
      WebkitTextFillColor: c.textDark,
      caretColor: c.textDark,
    },
  };

  const buttonSx = {
    py: 1.4,
    borderRadius: '100px',
    fontSize: '1rem',
    fontWeight: 700,
    textTransform: 'none',
    backgroundColor: c.accent,
    color: '#fff',
    '&:hover': { backgroundColor: '#7a68c9' },
    '&.Mui-disabled': { backgroundColor: `${c.accent}60`, color: '#fff9' },
  };

  const subtitle = showSecretAccess
    ? (secretMode === 'signup' ? 'Create your account' : 'Enter your chess.com username')
    : mode === 'signup'
    ? 'Coming soon'
    : 'Sign in to your account';

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: c.bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: 2,
    }}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <Box sx={{
          width: '100%',
          maxWidth: 420,
          backgroundColor: c.cardBg,
          borderRadius: '16px',
          border: `1px solid ${c.cardBorder}`,
          p: 4,
        }}>
          {/* Logo / Title */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <img
              src={c.mode === 'dark' ? '/logo-light.svg' : '/logo.svg'}
              alt="Destrier"
              onClick={handleLogoClick}
              style={{ width: 58, height: 58, marginBottom: 0, cursor: 'pointer', userSelect: 'none' }}
            />
            <Typography sx={{ fontSize: '1.8rem', fontWeight: 800, color: c.textDark, letterSpacing: '-0.02em' }}>
              Destrier<span style={{ color: c.accent }}>.ai</span>
            </Typography>
            <Typography sx={{ fontSize: '0.95rem', color: c.textMuted, mt: 0.5 }}>
              {subtitle}
            </Typography>
          </Box>

          <AnimatePresence mode="wait">
            {/* ── Step 2: Chess.com username ── */}
            {/* ── Login form ── */}
            {mode === 'login' && (
              <motion.div key="login" initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 30 }} transition={{ duration: 0.25 }}>
                <form onSubmit={handleLogin}>
                  <TextField
                    fullWidth label="Email" type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)} sx={textFieldSx}
                  />
                  <TextField
                    fullWidth label="Password" type="password" value={password}
                    onChange={(e) => setPassword(e.target.value)} sx={{ ...textFieldSx, mb: 3 }}
                  />

                  {error && (
                    <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}>
                      <Typography sx={{ color: '#E4446E', fontSize: '0.85rem', mb: 2, textAlign: 'center' }}>{error}</Typography>
                    </motion.div>
                  )}
                  {success && (
                    <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}>
                      <Typography sx={{ color: '#00A5C4', fontSize: '0.85rem', mb: 2, textAlign: 'center' }}>{success}</Typography>
                    </motion.div>
                  )}

                  <Button type="submit" fullWidth disabled={loading} sx={buttonSx}>
                    {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Sign In'}
                  </Button>
                </form>

                <Box sx={{ textAlign: 'center', mt: 2.5 }}>
                  <Typography sx={{ color: c.textMuted, fontSize: '0.88rem' }}>
                    Don't have an account?{' '}
                    <span onClick={toggleMode} style={{ color: c.accent, cursor: 'pointer', fontWeight: 600 }}>Sign up</span>
                  </Typography>
                </Box>
              </motion.div>
            )}

            {/* ── Signup / Secret access ── */}
            {mode === 'signup' && !showSecretAccess && (
              <motion.div key="signup" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.25 }}>
                <Typography sx={{ color: c.textDark, fontSize: '0.95rem', textAlign: 'center', lineHeight: 1.7, mb: 3 }}>
                  We haven't launched yet! Join our waitlist. You'll be able to create your account and link your chess.com username at launch.
                </Typography>

                {waitlistStatus === 'success' || waitlistStatus === 'already' ? (
                  <Box sx={{ backgroundColor: `${c.accent}15`, border: `1px solid ${c.accent}30`, borderRadius: '14px', py: 2, px: 3, textAlign: 'center' }}>
                    <Typography sx={{ color: c.textDark, fontSize: '0.95rem', fontWeight: 700 }}>
                      {waitlistStatus === 'already' ? "You're already on the list!" : "You're in!"}
                    </Typography>
                    {waitlistStatus === 'success' && (
                      <Typography sx={{ color: c.textDark, fontSize: '0.85rem', mt: 0.5 }}>
                        Check your emails{waitlistPosition ? `, you're #${waitlistPosition} on the waitlist.` : '!'}
                      </Typography>
                    )}
                    <Typography sx={{ color: c.textMuted, fontSize: '0.85rem', mt: 0.5 }}>
                      We'll notify you as soon as Destrier is ready.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <TextField
                      fullWidth label="Email" type="email" value={waitlistEmail}
                      onChange={(e) => setWaitlistEmail(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') document.getElementById('waitlist-btn-login').click(); }}
                      sx={{ ...textFieldSx, mb: 2 }}
                    />
                    <Button
                      id="waitlist-btn-login" fullWidth disabled={waitlistStatus === 'loading'}
                      onClick={async () => {
                        const trimmed = waitlistEmail.trim();
                        if (!trimmed || !trimmed.includes('@')) { setError('Please enter a valid email.'); return; }
                        setError('');
                        setWaitlistStatus('loading');
                        try {
                          const res = await fetch(`${API_BASE}/waitlist`, {
                            method: 'POST', headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ email: trimmed }),
                          });
                          const data = await res.json();
                          if (!res.ok) { setError(data.detail || 'Something went wrong'); setWaitlistStatus('idle'); return; }
                          if (data.position) { setWaitlistPosition(data.position); trackWaitlistSignup(data.position); }
                          else { trackWaitlistSignup(); }
                          setWaitlistStatus(data.already_registered ? 'already' : 'success');
                        } catch { setError('Connection failed. Try again.'); setWaitlistStatus('idle'); trackErrorDisplayed('login', 'waitlist_connection_failed'); }
                      }}
                      sx={{
                        ...buttonSx,
                        backgroundColor: '#ECE2DB',
                        color: '#22222e',
                        '&:hover': { backgroundColor: '#ECE2DB', filter: 'brightness(0.92)' },
                        '&.Mui-disabled': { backgroundColor: '#ECE2DB', color: '#22222e', opacity: 0.6 },
                      }}
                    >
                      {waitlistStatus === 'loading' ? <CircularProgress size={22} sx={{ color: '#22222e' }} /> : 'Join the Waitlist'}
                    </Button>
                  </>
                )}

                {error && mode === 'signup' && (
                  <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}>
                    <Typography sx={{ color: '#E4446E', fontSize: '0.85rem', mt: 2, textAlign: 'center' }}>{error}</Typography>
                  </motion.div>
                )}

                <Box sx={{ textAlign: 'center', mt: 2.5 }}>
                  <Typography sx={{ color: c.textMuted, fontSize: '0.88rem' }}>
                    Already have an account?{' '}
                    <span onClick={toggleMode} style={{ color: c.accent, cursor: 'pointer', fontWeight: 600 }}>Sign in</span>
                  </Typography>
                </Box>
              </motion.div>
            )}

            {/* ── Secret direct access ── */}
            {showSecretAccess && (
              <motion.div key="secret" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
                {success ? (
                  <Box sx={{ backgroundColor: `${c.accent}15`, border: `1px solid ${c.accent}30`, borderRadius: '14px', py: 2, px: 3, textAlign: 'center' }}>
                    <Typography sx={{ color: c.textDark, fontSize: '0.95rem', fontWeight: 700 }}>{success}</Typography>
                    <Box sx={{ mt: 2 }}>
                      <span onClick={() => { setMode('login'); setSuccess(''); setShowSecretAccess(false); setSecretMode('access'); }} style={{ color: c.accent, cursor: 'pointer', fontWeight: 600, fontSize: '0.95rem' }}>Sign in now</span>
                    </Box>
                  </Box>
                ) : secretMode === 'signup' ? (
                  <form onSubmit={handleSecretSignup}>
                    <TextField fullWidth label="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} sx={textFieldSx} />
                    <TextField fullWidth label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} sx={textFieldSx} />
                    <TextField fullWidth label="Chess.com Username" value={chessUsername} onChange={(e) => setChessUsername(e.target.value)} placeholder="e.g. hikaru" sx={textFieldSx} />
                    <TextField fullWidth label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} sx={{ ...textFieldSx, mb: 3 }} />
                    {error && (
                      <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}>
                        <Typography sx={{ color: '#E4446E', fontSize: '0.85rem', mb: 2, textAlign: 'center' }}>{error}</Typography>
                      </motion.div>
                    )}
                    <Button type="submit" fullWidth disabled={loading} sx={buttonSx}>
                      {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Create Account'}
                    </Button>
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                      <Typography sx={{ color: c.textMuted, fontSize: '0.88rem' }}>
                        Already have an account?{' '}
                        <span onClick={() => { setSecretMode('access'); setError(''); }} style={{ color: c.accent, cursor: 'pointer', fontWeight: 600 }}>Go to dashboard</span>
                      </Typography>
                    </Box>
                  </form>
                ) : (
                  <>
                    <TextField
                      fullWidth label="Chess.com Username" value={chessUsername}
                      onChange={(e) => setChessUsername(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSecretAccess(); }}
                      placeholder="e.g. hikaru"
                      sx={{ ...textFieldSx, mb: 3 }}
                    />
                    {error && (
                      <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}>
                        <Typography sx={{ color: '#E4446E', fontSize: '0.85rem', mb: 2, textAlign: 'center' }}>{error}</Typography>
                      </motion.div>
                    )}
                    <Button fullWidth disabled={loading} onClick={handleSecretAccess} sx={buttonSx}>
                      {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Go to Dashboard'}
                    </Button>
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                      <Typography sx={{ color: c.textMuted, fontSize: '0.88rem' }}>
                        Need an account?{' '}
                        <span onClick={() => { setSecretMode('signup'); setError(''); }} style={{ color: c.accent, cursor: 'pointer', fontWeight: 600 }}>Create one</span>
                      </Typography>
                    </Box>
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Back to home */}
          <Box sx={{ textAlign: 'center', mt: 1.5 }}>
            <span
              onClick={() => navigate('/')}
              style={{ color: c.textMuted, cursor: 'pointer', fontSize: '0.82rem' }}
            >
              Back to home
            </span>
          </Box>
        </Box>
      </motion.div>
    </Box>
  );
}
